import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import * as moment from "moment";
import { ToastrService } from "ngx-toastr";
import { TranslatorService } from 'src/app/services/translate.service';

@Component({
    selector: 'planner-riepilogo-dialog',
    templateUrl: './planner-riepilogo-dialog.html',
    styleUrls: ['./planner-riepilogo-dialog.scss'],
})
export class plannerRiepilogoDialogComponent implements OnInit {

    displayDate: string;
    displayDataRange: string;
    startDate: any = this.data.startDate;
    endDate: any = this.data.endDate;

    // PLANNED
    operators: { name: string; commessa: string; hours: number }[] = [];
    displayedColumns: string[] = ['name', 'commessa', 'hours'];
    noOrdersMessage: string;

    // ASSENZE (vista compatta unica)
    datesYMD: string[] = []; // giorni dell'intervallo in formato YYYY-MM-DD
    compactRows: Array<{
        orderCode: string;
        building: string
        userId: number;
        name: string;
        role: string;
        causalCode: string | null;       // 👈 nuovo
        hours: number | null;            // 👈 nuovo (gruppo ore)
        ranges: Array<{ from: string; to: string; days: number }>;
    }> = [];
    // ⬇️ in cima alla classe
    private allowedIdSet = new Set<string>();

    private isAllowed(uid: number | string): boolean {
        return this.allowedIdSet.has(String(uid));
    }


    private absencesDetailsByUser: Record<string, Array<{ ymd: string; hours: number | null; causalCode: string | null }>> = {};

    // aggiungi le colonne nella tabella
    absenceDisplayedColumns: string[] = ['order', 'name', 'causal', 'hours', 'ranges'];

    // 👇 cambia la union
    viewMode: 'planned' | 'absences' | 'unplanned' = 'planned';

    // 👇 nuova tabella "Non pianificati"
    unplannedRows: Array<{ id: number; name: string; role: string; office: string | null }> = [];
    unplannedDisplayedColumns: string[] = ['name', 'role'];


    // assenze lookup
    private absencesByUserSet: Record<string, Set<string>> = {};

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: DialogData,
        private toastr: ToastrService,
        public translate: TranslatorService
    ) { }

    ngOnInit(): void {
        console.log('Riepilogo data', this.data);
        // 1) costruisci whitelist dagli id in resourcesIndex (getRisorse)
        const idx = this.data.resourcesIndex || {};
        Object.keys(idx).forEach(id => this.allowedIdSet.add(String(id)));

        // fallback se (per qualche motivo) resourcesIndex non c'è:
        if (this.allowedIdSet.size === 0 && Array.isArray(this.data.unplannedResources)) {
            for (const r of this.data.unplannedResources) this.allowedIdSet.add(String(r.id));
        }

        this.updateDisplayDate();
        this.processSummary(this.data.summary);

        // 2) carica assenze e FILTRA subito sui soli ammessi
        const rawDetails = this.data.absencesDetailsByUser || {};
        this.absencesDetailsByUser = Object.fromEntries(
            Object.entries(rawDetails).filter(([k]) => this.isAllowed(k))
        );

        // 3) ricostruisci Set assenze, filtrato
        const rawSets = this.data.absencesByUser || {};
        this.absencesByUserSet = Object.fromEntries(
            Object.entries(rawSets)
                .filter(([k]) => this.isAllowed(k))
                .map(([k, arr]) => [k, new Set(arr as string[])])
        );

        // 4) giorni dell’intervallo
        this.datesYMD = this.buildDatesYMD(this.startDate, this.endDate);

        // 5) costruisci subito le viste
        this.buildAbsenceListAllOrders();
        this.unplannedRows = (this.data as any).unplannedResources
            ? (this.data as any).unplannedResources
                .map((r: any) => ({ id: r.id, name: r.name, role: r.ruolo, office: r.NomeSede }))
                .sort((a: any, b: any) => a.role === b.role ? a.name.localeCompare(b.name) : a.role.localeCompare(b.role))
            : [];
    }

    // ====== HEADER DATE ======
    updateDisplayDate(): void {
        this.displayDataRange = `<strong>${this.formatDateWithMoment(this.data.dataRangeStart)}</strong> - <strong>${this.formatDateWithMoment(this.data.dataRangeEnd)}</strong>`;

        const isInRange = moment(this.startDate).isBetween(this.data.dataRangeStart, this.data.dataRangeEnd, 'day', '[]') &&
            moment(this.endDate).isBetween(this.data.dataRangeStart, this.data.dataRangeEnd, 'day', '[]');

        if (isInRange) {
            this.noOrdersMessage = this.translate.instant('PLANNER_RIEPILOGO.NO_PLANNED_ORDERS');
        } else {
            this.noOrdersMessage = `${this.translate.instant('PLANNER_RIEPILOGO.OUT_OF_RANGE')} ${this.displayDataRange}`;
        }

        const dateDifference = this.getDateDifference(this.startDate, this.endDate);
        if (dateDifference > 5) {
            this.displayDate = `${this.formatDate(this.startDate)} - ${this.formatDate(this.endDate)}`;
        } else {
            this.displayDate = `${this.formatDate(this.startDate)}`;
        }
        this.displayDataRange = `${this.formatDateWithMoment(this.data.dataRangeStart)} - ${this.formatDateWithMoment(this.data.dataRangeEnd)}`;
    }

    // ====== PLANNED ======
    processSummary(summary: any): void {
        const operatorData: { name: string; commessa: string; hours: number }[] = [];

        Object.keys(summary).forEach(dateKey => {
            const data = summary[dateKey];
            data.commesse.forEach((commessa: any) => {
                commessa.operators.forEach((operator: { name: string, role: string, hours: number, building: string }) => {
                    operatorData.push({
                        name: `${operator.name} (${operator.role})`,
                        commessa: `${commessa.code} (${commessa.building})`,
                        hours: operator.hours || 0
                    });
                });
            });
        });

        const groupedOperators = operatorData.reduce((acc, operator) => {
            if (!acc[operator.name]) {
                acc[operator.name] = {
                    commesse: new Map<string, Set<string>>(),
                    totalHours: 0
                };
            }
            if (!acc[operator.name].commesse.has(operator.commessa)) {
                acc[operator.name].commesse.set(operator.commessa, new Set([operator.commessa]));
            }
            acc[operator.name].totalHours += operator.hours;
            return acc;
        }, {} as { [key: string]: { commesse: Map<string, Set<string>>, totalHours: number } });

        const allOperators: { name: string; commessa: string; hours: number }[] = [];
        Object.keys(groupedOperators).forEach(operatorName => {
            const operatorEntry = groupedOperators[operatorName];
            const commesseArray = Array.from(operatorEntry.commesse.keys());
            const commessa = commesseArray.join('\n');
            allOperators.push({
                name: operatorName,
                commessa: commessa,
                hours: operatorEntry.totalHours
            });
        });
        allOperators.sort((a, b) => a.name.localeCompare(b.name));
        this.operators = allOperators;
    }

    get uniqueOperatorCount(): number {
        return new Set(this.operators.map(op => op.name)).size;
    }

    private buildDatesYMD(start: Date | string, end: Date | string): string[] {
        const s = new Date(start);
        const e = new Date(end);

        // normalizza a date-only locali
        let d = new Date(s.getFullYear(), s.getMonth(), s.getDate());
        const last = new Date(e.getFullYear(), e.getMonth(), e.getDate());

        const out: string[] = [];
        while (d.getTime() <= last.getTime()) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            out.push(`${y}-${m}-${day}`);
            d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
        }
        return out;
    }



    formatYMDHeader(ymd: string): string {
        return moment(ymd, 'YYYY-MM-DD').format('DD/MM');
    }
    formatDateWithMoment(date: Date | string): string {
        return moment(date).format('DD/MM/YYYY');
    }
    formatDate(date: Date): string {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }
    getDateDifference(date1: Date, date2: Date): number {
        const timeDifference = Math.abs(date2.getTime() - date1.getTime());
        return Math.ceil(timeDifference / (1000 * 3600 * 24));
    }

    switchTo(view: 'planned' | 'absences' | 'unplanned') {
        this.viewMode = view;
        if (view === 'absences') this.buildAbsenceListAllOrders();
    }
    roleCss(role: string): string {
        const r = (role || '').toLowerCase();
        return r.includes('capo') ? 'role-tl' : 'role-as';
    }


    // ====== ASSENZE: vista compatta unica (commessa + operatore + range) ======
    private buildRanges(sortedAbsentYmd: string[]): Array<{ from: string; to: string; days: number }> {
        // ✨ dedup + sort qui
        const days = Array.from(new Set(sortedAbsentYmd)).sort();
        if (!days.length) return [];

        const out: Array<{ from: string; to: string; days: number }> = [];
        let start = days[0];
        let prev = days[0];

        const pushRange = (a: string, b: string) => {
            const span = moment(b, 'YYYY-MM-DD').diff(moment(a, 'YYYY-MM-DD'), 'days') + 1;
            out.push({ from: a, to: b, days: span });
        };

        for (let i = 1; i < days.length; i++) {
            const cur = days[i];
            const expectedNext = moment(prev, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD');
            if (cur !== expectedNext) {
                pushRange(start, prev);
                start = cur;
            }
            prev = cur;
        }
        pushRange(start, prev);
        return out;
    }


    /** Per ogni commessa presente nel summary e per ogni operatore pianificato su quella commessa,
     * calcola i giorni di assenza dentro l'intervallo (datesYMD) e li comprime in range. */
    private buildAbsenceListAllOrders(): void {
        const s = this.data.summary || {};
        const orderBuildingByCode = new Map<string, string>();

        // 1) Mappa: orderCode -> Map<userId, { name, role }>
        const byOrder = new Map<string, Map<number, { name: string; role: string }>>();
        for (const dateKey of Object.keys(s)) {
            const day = s[dateKey];
            for (const c of (day.commesse || [])) {
                if (!orderBuildingByCode.has(c.code)) {
                    const b = (c?.building ?? '').toString().trim();
                    if (b) orderBuildingByCode.set(c.code, b);
                    else orderBuildingByCode.set(c.code, ''); // mantieni la chiave, anche se vuoto
                }
                if (!byOrder.has(c.code)) byOrder.set(c.code, new Map());
                const bucket = byOrder.get(c.code)!;
                for (const op of (c.operators || [])) {
                    const uid = Number(op.id);
                    if (!bucket.has(uid)) bucket.set(uid, { name: op.name, role: op.role });
                }
            }
        }

        const rows: typeof this.compactRows = [];
        const inRange = new Set(this.datesYMD);

        // —— tieni traccia degli userId già inclusi nelle righe “per commessa”
        const includedUserIds = new Set<number>();

        // 2) Righe per COMMESSA (come prima)
        for (const [orderCode, usersMap] of byOrder.entries()) {
            const buildingForOrder = orderBuildingByCode.get(orderCode) || '';

            for (const [uid, meta] of usersMap.entries()) {
                // filtra alle risorse del componente padre
                if (!this.data.resourcesIndex || !this.data.resourcesIndex[String(uid)]) continue;

                const details = (this.absencesDetailsByUser[String(uid)] || []).filter(d => inRange.has(d.ymd));

                if (details.length) {
                    const groups = new Map<string, { hours: number | null; causalCode: string | null; days: string[] }>();
                    for (const d of details) {
                        const key = `${d.hours ?? 'null'}|${d.causalCode ?? ''}`;
                        if (!groups.has(key)) groups.set(key, { hours: d.hours ?? null, causalCode: d.causalCode ?? null, days: [] });
                        groups.get(key)!.days.push(d.ymd);
                    }
                    for (const g of groups.values()) {
                        const sorted = g.days.sort();
                        const ranges = this.buildRanges(sorted);
                        rows.push({
                            orderCode,
                            building: buildingForOrder,
                            userId: uid,
                            name: meta.name,
                            role: meta.role,
                            hours: g.hours,
                            causalCode: g.causalCode,
                            ranges
                        });
                        includedUserIds.add(uid);
                    }
                } else {
                    const set = this.absencesByUserSet[String(uid)] || new Set<string>();
                    const absentWithin = this.datesYMD.filter(d => set.has(d));
                    if (absentWithin.length) {
                        const ranges = this.buildRanges(absentWithin.sort());
                        rows.push({
                            orderCode,
                            building: buildingForOrder,
                            userId: uid,
                            name: meta.name,
                            role: meta.role,
                            hours: null,
                            causalCode: null,
                            ranges
                        });
                        includedUserIds.add(uid);
                    }
                }
            }
        }

        // 3) BLOCCO “ALTRI ASSENTI” (non su commessa)
        //    - solo utenti provenienti da getRisorse (resourcesIndex)
        //    - esclusi quelli già comparsi sopra
        const othersLabel = this.translate?.instant?.('PLANNER_RIEPILOGO.ABSENCES_UNASSIGNED') || 'Altri assenti';
        for (const [k, meta] of Object.entries(this.data.resourcesIndex || {})) {
            const uid = Number(k);
            if (includedUserIds.has(uid)) continue; // già mostrato su una commessa

            // detagli con ore/causale se disponibili
            const details = (this.absencesDetailsByUser[String(uid)] || []).filter(d => inRange.has(d.ymd));
            if (details.length) {
                const groups = new Map<string, { hours: number | null; causalCode: string | null; days: string[] }>();
                for (const d of details) {
                    const key = `${d.hours ?? 'null'}|${d.causalCode ?? ''}`;
                    if (!groups.has(key)) groups.set(key, { hours: d.hours ?? null, causalCode: d.causalCode ?? null, days: [] });
                    groups.get(key)!.days.push(d.ymd);
                }
                for (const g of groups.values()) {
                    const sorted = g.days.sort();
                    const ranges = this.buildRanges(sorted);
                    rows.push({
                        orderCode: othersLabel,         // 👈 etichetta “Altri assenti”
                        building: '',
                        userId: uid,
                        name: meta.name,
                        role: meta.ruolo ?? '',
                        hours: g.hours,
                        causalCode: g.causalCode,
                        ranges
                    });
                }
                continue;
            }

            // fallback: solo Set-giorni
            const set = this.absencesByUserSet[String(uid)] || new Set<string>();
            const absentWithin = this.datesYMD.filter(d => set.has(d));
            if (absentWithin.length) {
                const ranges = this.buildRanges(absentWithin.sort());
                rows.push({
                    orderCode: othersLabel,
                    building: '',
                    userId: uid,
                    name: meta.name,
                    role: meta.ruolo ?? '',
                    hours: null,
                    causalCode: null,
                    ranges
                });
            }
        }

        const isOthers = (s: string) =>
            (s ?? '').trim().toLowerCase() === othersLabel.trim().toLowerCase();

        rows.sort((a, b) => {
            // 1) gruppo: commesse (0) prima, "Altri assenti" (1) dopo
            const gA = isOthers(a.orderCode) ? 1 : 0;
            const gB = isOthers(b.orderCode) ? 1 : 0;
            if (gA !== gB) return gA - gB;

            // 2) dentro il gruppo:
            //    - per commesse: per codice commessa -> nome
            //    - per "altri": solo per nome (l’orderCode è la stessa etichetta)
            const cmp = gA === 0
                ? a.orderCode.localeCompare(b.orderCode, 'it', { numeric: true, sensitivity: 'base' }) ||
                a.name.localeCompare(b.name, 'it', { numeric: true, sensitivity: 'base' })
                : a.name.localeCompare(b.name, 'it', { numeric: true, sensitivity: 'base' });

            if (cmp !== 0) return cmp;

            // 3) a parità, ore desc (i null in fondo)
            const hr = (h: number | null) => (h == null ? -1 : h);
            return hr(b.hours) - hr(a.hours);
        });


        this.compactRows = rows;
    }

    private metaFor(uid: number | string) {
        const m = this.data.resourcesIndex?.[String(uid)];
        return {
            name: m?.name ?? String(uid),   // se manca, mostra l’id (ma ora non dovrebbe più capitare)
            role: m?.ruolo ?? '',
            office: m?.NomeSede ?? null
        };
    }



}

interface DialogData {
    resourcesIndex?: Record<string, { name: string; ruolo: string; NomeSede: string | null }>;
    startDate: Date;
    endDate: Date;
    summary: any;
    dataRangeStart: Date;
    dataRangeEnd: Date;
    absencesByUser: Record<string, string[]>;
    absencesDetailsByUser?: Record<string, Array<{ ymd: string; hours: number | null; causalCode: string | null }>>; // 👈 nuovo
    unplannedResources?: Array<{ id: number; name: string; ruolo: string; NomeSede: string | null }>;
}


